import React, { useEffect, useState, useRef } from 'react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { useNudgeStore } from '../store/nudgeStore';

const SESSION_KEY = 'install_banner_dismissed_session';
const AUTO_HIDE_MS = 10000;
const NUDGE_ID = 'install';

/**
 * "Install Porte17" nudge.
 *
 * - Uses sessionStorage (not localStorage) so it comes back every new
 *   session/day, as requested - it only stays quiet for the rest of the
 *   CURRENT session once dismissed or auto-hidden.
 * - Auto-hides after 10s if the user doesn't interact.
 * - Android/Chrome: uses the real `beforeinstallprompt` flow.
 * - iOS Safari: there is no install API, so it shows manual steps instead.
 * - Claims a shared "nudge slot" so it never visually stacks with the
 *   push-notification nudge.
 */
const InstallAppBanner = () => {
  const { isInstalled, platform, canPromptNatively, promptInstall } = useInstallPrompt();
  const { claim, release } = useNudgeStore();

  const [visible, setVisible] = useState(false);
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (isInstalled) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Small delay so it doesn't fight with initial page load, and gives
    // Chrome a moment to fire beforeinstallprompt if it's going to.
    const showTimer = setTimeout(() => {
      if (claim(NUDGE_ID)) setVisible(true);
    }, 2000);

    return () => clearTimeout(showTimer);
  }, [isInstalled, claim]);

  useEffect(() => {
    if (!visible) return;
    hideTimerRef.current = setTimeout(dismiss, AUTO_HIDE_MS);
    return () => clearTimeout(hideTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setVisible(false);
    setShowIOSSteps(false);
    release(NUDGE_ID);
  };

  const handleInstallClick = async () => {
    if (platform === 'ios') {
      // Show manual steps instead of auto-hiding/dismissing.
      setShowIOSSteps(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      return;
    }
    if (canPromptNatively) {
      await promptInstall();
    }
    dismiss();
  };

  if (isInstalled || !visible) return null;

  return (
    <div className="fixed bottom-4 inset-x-4 sm:left-4 sm:right-auto sm:w-96 z-[9997] animate-fadeIn">
      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-100 p-5">
        <button
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 text-lg leading-none w-6 h-6 flex items-center justify-center"
        >
          ✕
        </button>

        {!showIOSSteps ? (
          <>
            <h3 className="text-sm font-bold text-slate-900 pr-6 mb-1">Install Porte17</h3>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed pr-2">
              Add Porte17 to your home screen for faster access, offline support, and a
              full-screen experience.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleInstallClick}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider transition-all"
              >
                Install
              </button>
              <button
                onClick={dismiss}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider transition-all"
              >
                Not now
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-sm font-bold text-slate-900 pr-6 mb-2">Add to Home Screen</h3>
            <ol className="text-xs text-slate-600 list-decimal list-inside space-y-1 mb-4">
              <li>Tap the Share icon in Safari's toolbar</li>
              <li>Scroll down and tap "Add to Home Screen"</li>
              <li>Tap "Add" in the top right corner</li>
            </ol>
            <button
              onClick={dismiss}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider transition-all"
            >
              Got it
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default InstallAppBanner;
